import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createAction,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { t } from '@vk-hr-tek/core/translations';
import { AppError } from '@vk-hr-tek/core/error';
import { showError, showNotification } from '@vk-hr-tek/core/notifications';

import {
  AddUserGroupRequest,
  CheckCompanyGroupCreationParams,
  CheckCompanyGroupCreationRequest,
  CheckCompanyGroupCreationResponseItem,
  CompanyEventsStat,
  CompanyGroupUsers,
  CreateCompanyGroupParams,
  CreateCompanyGroupRequest,
  DeleteUserGroupRequest,
  GetCompanyGroupsParams,
  GetCompanyGroupUsersParams,
  Group,
  SettingsService,
} from '@app/gen/settings';
import {
  UsersService,
  UserListRequestFilter,
  UserListItem,
} from '@app/gen/users';
import { Entity } from '@app/types';

import { ThunkExtra, ThunkValuesAndActions } from '../../../../app/store';
import { SettingsGroupsRouter, GroupUser } from '../../../types';
import { SettingsState, SettingsWithRootState } from '../../settings.state';
import { SettingsDetailMapper } from '../../../mappers';
import { GroupNotificationsService } from '../../../services';
import { updateUserGroups } from '../../../../user';

export const getCompanyGroupUser = createAsyncThunk<
  GroupUser[],
  GetCompanyGroupUsersParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanyGroupUser',
  async (getCompanyGroupUserDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);
      const result = await settingsService.getCompanyGroupUsers({
        params: { company_id: getCompanyGroupUserDto.company_id },
      });

      const mapper = await inject(SettingsDetailMapper);
      return mapper.processCompanyGroupUser(result);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanyUsers = createAsyncThunk<
  UserListItem[],
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanyUsers',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const usersService = await inject(UsersService);

      const result = await usersService.userList({
        body: {
          filter: { company_id: companyId } as UserListRequestFilter,
        },
      });

      return result.users;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const removeRole = createAsyncThunk<
  void,
  ThunkValuesAndActions<DeleteUserGroupRequest & { company_id: string }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/removeRole',
  async (
    { values, actions },
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).deleteUserGroup({
        body: {
          user_id: values.user_id,
          group_id: values.group_id,
        },
      });

      dispatch(
        showNotification(
          t('settings.actions.group.employeeRoleWasDeletedText'),
        ),
      );
      actions.resolve(null);
      dispatch(getCompanyGroupUser({ company_id: values.company_id }));

      const myUserId = getState().user.data?.id;

      if (myUserId === values.user_id) {
        dispatch(updateUserGroups());
      }
      actions.resolve(null);
    } catch (err) {
      dispatch(
        showError(
          t('settings.actions.group.errorWhileEmployeeRoleDeletionText'),
        ),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addRole = createAsyncThunk<
  void,
  ThunkValuesAndActions<AddUserGroupRequest & { company_id: string }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addRole',
  async (
    { values, actions },
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).addUserGroup({
        body: {
          user_id: values.user_id,
          group_id: values.group_id,
          ...(!!values.profiles?.length ? { profiles: values.profiles } : {}),
        },
      });

      dispatch(
        showNotification(t('settings.actions.group.employeeRoleAddedText')),
      );
      actions.resolve(null);
      dispatch(getCompanyGroupUser({ company_id: values.company_id }));

      const myUserId = getState().user.data?.id;

      if (myUserId === values.user_id) {
        dispatch(updateUserGroups());
      }
      actions.resolve(null);
    } catch (err) {
      dispatch(
        showError(t('settings.actions.group.errorWhileEmployeeRoleAddingText')),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addRoleCompany = createAsyncThunk<
  void,
  ThunkValuesAndActions<CreateCompanyGroupParams & CreateCompanyGroupRequest>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addRoleCompany',
  async (
    { values, actions },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const settingsService = inject(SettingsService);
      const router = inject<SettingsGroupsRouter>(SettingsGroupsRouter);
      const groupNotificationsService = inject(GroupNotificationsService);

      const response = await settingsService.createCompanyGroup({
        params: { company_id: values.company_id },
        body: {
          name: values.name,
          parent_id: values.parent_id,
          all_companies: values.all_companies,
        },
      });

      const result = response.result;

      const notifications =
        groupNotificationsService.convertCompaniesToNotifications(
          result,
          getState().settings.companies,
        );

      notifications.forEach(({ success, message }) => {
        dispatch(success ? showNotification(message) : showError(message));
      });

      router.goToList(`#${values.company_id}`);
      actions.resolve(null);
    } catch (err) {
      dispatch(showError('Не удалось добавить роль!'));
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const checkExistsCompanyRoles = createAsyncThunk<
  CheckCompanyGroupCreationResponseItem[],
  CheckCompanyGroupCreationParams & CheckCompanyGroupCreationRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/checkExistsCompanyRoles',
  async (
    { company_id, name, parent_id },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const settingsService = await inject(SettingsService);
      const response = await settingsService.checkCompanyGroupCreation({
        params: { company_id },
        body: {
          name: name,
          ...(parent_id ? { parent_id } : {}),
        },
      });

      return response?.result || [];
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const checkCompanyListAccount = createAsyncThunk<
  CheckCompanyGroupCreationResponseItem[],
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/checkCompanyListAccount',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);
      const response = await settingsService.checkCompanyGroupCreation({
        params: { company_id: companyId },
        body: { name: 'check_account_companies' },
      });

      return response?.result || [];
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);
export const resetExistsCompanyRoles = createAction<void>(
  'settings/resetExistsCompanyRoles',
);

export const getExistsCompanyRoles = createAsyncThunk<
  Group[],
  GetCompanyGroupsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getExistsCompanyRoles',
  async ({ company_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);
      const response = await settingsService.getCompanyGroups({
        params: { company_id },
      });

      return response.groups;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsGroupReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getCompanyGroupUser.pending, (state, { meta }) => {
    state.groupUsers[meta.arg.company_id] = {
      status: 'loading',
      error: null,
      groups: [],
    };
  });
  builder.addCase(getCompanyGroupUser.fulfilled, (state, { payload, meta }) => {
    state.groupUsers[meta.arg.company_id] = {
      status: 'complete',
      error: null,
      groups: payload || [],
    };
  });
  builder.addCase(
    getCompanyGroupUser.rejected,
    (state, { payload, error, meta }) => {
      state.groupUsers[meta.arg.company_id] = {
        status: 'failed',
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
        groups: [],
      };
    },
  );
  builder.addCase(getCompanyUsers.pending, (state) => {
    state.users = {
      status: 'loading',
      items: [],
      error: null,
    };
  });
  builder.addCase(getCompanyUsers.fulfilled, (state, { payload }) => {
    state.users = {
      status: 'complete',
      items: payload,
      error: null,
    };
  });
  builder.addCase(getCompanyUsers.rejected, (state, { payload, error }) => {
    state.users = {
      status: 'failed',
      items: [],
      error:
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError),
    };
  });
  builder.addCase(getExistsCompanyRoles.pending, (state) => {
    state.companyGroups = {
      status: 'loading',
      items: [],
      error: null,
    };
  });
  builder.addCase(getExistsCompanyRoles.fulfilled, (state, { payload }) => {
    state.companyGroups = {
      status: 'complete',
      items: payload,
      error: null,
    };
  });
  builder.addCase(
    getExistsCompanyRoles.rejected,
    (state, { payload, error }) => {
      state.companyGroups = {
        status: 'failed',
        items: [],
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
      };
    },
  );
  builder.addCase(checkExistsCompanyRoles.pending, (state) => {
    state.companyGroupsChecked = {
      status: 'loading',
      items: [],
      error: null,
    };
  });
  builder.addCase(checkExistsCompanyRoles.fulfilled, (state, { payload }) => {
    state.companyGroupsChecked = {
      status: 'complete',
      items: payload,
      error: null,
    };
  });
  builder.addCase(
    checkExistsCompanyRoles.rejected,
    (state, { payload, error }) => {
      state.companyGroupsChecked = {
        status: 'failed',
        items: [],
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
      };
    },
  );
  builder.addCase(checkCompanyListAccount.pending, (state) => {
    state.checkCompanyListAccount = {
      status: 'loading',
      items: [],
      error: null,
    };
  });
  builder.addCase(checkCompanyListAccount.fulfilled, (state, { payload }) => {
    state.checkCompanyListAccount = {
      status: 'complete',
      items: payload,
      error: null,
    };
  });
  builder.addCase(
    checkCompanyListAccount.rejected,
    (state, { payload, error }) => {
      state.checkCompanyListAccount = {
        status: 'failed',
        items: [],
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
      };
    },
  );
  builder.addCase(resetExistsCompanyRoles, (state) => {
    state.companyGroupsChecked = {
      status: 'idle',
      items: [],
      error: null,
    };
  });
};
